// Logic Model (API Request/Response)
export interface MerchantHeatmapApiResponse {
    active_coupons: number;
    active_coupons_limit: number;
    total_views: number;
    conversion_rate: number; // 0-100
    total_redemptions: number;
    coupon_performance: CouponPerformanceRow[];
    best_performing: {
        coupon_name: string;
        conversion_rate: number; // 0-100
        redemptions: number;
    };
    redemption_breakdown: {
        in_app: number;
        in_store: number;
    };
    locations: LocationData[];
    neighborhood_analysis: NeighborhoodAnalysis[];
}

export type CouponPerformanceLevel = 'excellent' | 'good' | 'average' | 'poor' | 'na';

export interface CouponPerformanceRow {
    coupon_name: string;
    views: number;
    redeemed_in_app: number;
    usage_in_store: number;
    conversion_rate: number | null; // null => N/A
    performance: CouponPerformanceLevel;
}

export interface LocationData {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    redemptions: number;
    percentage_of_total: number;
    top_coupon: string;
    redemption_rate: number; // 0-100, used to determine color (High >70%, Medium 50-70%, Low <50%)
    growth_percentage?: number; // From activity-levels API
    demographics?: string; // From activity-levels API (age_range)
}

export interface NeighborhoodAnalysis {
    area_name: string;
    total_redemptions: number;
    percentage_of_total: number;
    top_coupon: string;
    performance: 'excellent' | 'good' | 'needs_focus';
}

// Location Analytics Types
export interface RedemptionTrend {
    date: string;
    redemption_count: number;
}

export interface LocationPerformance {
    total_redemptions: number;
    top_location: {
        location_name: string;
        percentage: number;
    };
    avg_per_location: number;
}

export interface UserEngagement {
    avg_points_spent: number;
    top_event: {
        event_name: string;
        attendees_redeemed: number;
    };
}

export interface LocationAnalyticsData {
    peak_day: string;
    top_location: string;
    avg_points_spent: number;
    redemption_trends: RedemptionTrend[];
    location_performance: LocationPerformance;
    user_engagement: UserEngagement;
}

export interface MerchantHeatmapFilters {
    location?: string;
    location_ids?: string[]; // For API filter
    demographics?: string;
    demographics_age?: {
        min_age?: number;
        max_age?: number;
    }; // For API filter
    coupon_name?: string;
    coupon_ids?: string[]; // For API filter
    start_date?: string;
    end_date?: string;
    quick_filter?: 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';
}

// UI Model (State Management)
export interface MerchantHeatmapUIState {
    active_coupons: number;
    active_coupons_limit: number;
    total_views: number;
    conversion_rate: number;
    total_redemptions: number;
    coupon_performance: CouponPerformanceRow[];
    best_performing: {
        coupon_name: string;
        conversion_rate: number;
        redemptions: number;
    };
    redemption_breakdown: {
        in_app: number;
        in_store: number;
    };
    locations: LocationData[];
    neighborhood_analysis: NeighborhoodAnalysis[];
    location_analytics: LocationAnalyticsData | null;
    filters: MerchantHeatmapFilters;
    selectedLocation: LocationData | null;
    isLoading: boolean;
    error: string | null;
}

// Data Mapper
export const mapApiResponseToUI = (apiData: MerchantHeatmapApiResponse, filters: MerchantHeatmapFilters): MerchantHeatmapUIState => {
    return {
        ...apiData,
        location_analytics: null,
        filters,
        selectedLocation: null,
        isLoading: false,
        error: null,
    };
};

